// Catalog Shell - Main Shell Class
// ============================================================================

import {
    ShellConfig,
    ShellAPI,
    ShellEvent,
    ShellEventCallback,
    ScreenSize,
    Breakpoints,
    DEFAULT_BREAKPOINTS,
    WorkspaceDefinition,
    PaneType,
    PaneInstance,
    PaneInstanceSpec,
    ActionContext,
    ActionDefinition,
    SurfaceAPI,
    PaneTypeRenderContext,
    ExportedShellConfig,
    ImportResult,
    StoredShellConfig,
    StoredWorkspaceConfig,
    GridLayoutConfig,
    FlowLayoutConfig,
    StandalonePopoutConfig,
    SHELL_VERSION,
    generateId,
} from './types';
import { PaneTypeRegistry } from './pane-type-registry';
import { ActionRegistry, createBuiltinActions } from './action-registry';
import { PopoutManager } from './popout-manager';
import { ModalManager } from './modal-manager';
import type { ModalOptions, ModalHandle, ConfirmOptions, PromptOptions } from './modal-manager';
import { ToastManager } from './toast-manager';
import type { ToastOptions, ToastHandle } from './toast-manager';
import { EventEmitter } from '../utils/event-emitter';
import { createElement, clearElement, debounce } from '../utils/dom';
import { DisposableStore } from '../components/disposable';
import { createDropdownMenu } from '../components/dropdown-menu';
import type { DropdownMenu } from '../components/dropdown-menu';
import { icons } from '../utils/icons';

export class Shell implements ShellAPI {
    private container: HTMLElement;
    private config: ShellConfig;
    private storageKey: string;

    private typeRegistry: PaneTypeRegistry;
    private actionRegistry: ActionRegistry;
    private events: EventEmitter<ShellEvent>;

    private activeWorkspaceId: string;
    private screenSizeOverride: ScreenSize | null = null;
    private breakpoints: Breakpoints;

    // DOM elements
    private rootElement: HTMLElement | null = null;
    private workspaceSwitcherElement: HTMLElement | null = null;
    private rootSurfaceContainer: HTMLElement | null = null;
    private actionBarElement: HTMLElement | null = null;
    private actionBarPrefixElements: HTMLElement[] = [];
    // The pane that was focused when each workspace was last shown, restored
    // on return so the action bar and title rows describe THIS workspace.
    private workspaceFocusedPane: Map<string, string> = new Map();
    private moreMenu: DropdownMenu | null = null;

    // Instance tracking (global across all workspaces)
    private paneInstances: Map<string, PaneInstance> = new Map();
    private surfaceInstances: Map<string, SurfaceAPI> = new Map();
    private rootSurface: SurfaceAPI | null = null;

    // Workspace state caching - keep workspaces alive when switching
    private workspaceElements: Map<string, HTMLElement> = new Map();
    private workspaceRootSurfaces: Map<string, SurfaceAPI> = new Map();
    private workspacePaneInstances: Map<string, Set<string>> = new Map();
    private workspaceSurfaceInstances: Map<string, Set<string>> = new Map();

    // Loaded workspace configs from localStorage (for restoring layouts)
    private storedWorkspaceConfigs: { [workspaceId: string]: StoredWorkspaceConfig } = {};

    // Auto-managed disposables per pane instance
    private paneDisposables: Map<string, DisposableStore> = new Map();

    // Popout manager
    private popoutManager: PopoutManager;

    // Modal and toast managers
    private modalManager: ModalManager | null = null;
    private toastManager: ToastManager | null = null;

    // Hidden workspaces (runtime-controlled visibility)
    private hiddenWorkspaces: Set<string> = new Set();

    // Runtime-added (dynamic) workspaces — e.g. editors created on demand.
    // Merged with config.workspaces for every lookup; persisted across reloads.
    private runtimeWorkspaces: WorkspaceDefinition[] = [];

    // Title strips mounted above a module-container workspace root, keyed by
    // the ROOT pane's instanceId (see renderWorkspaceRootHeader).
    private workspaceRootHeaders: Map<string, HTMLElement> = new Map();

    // Per-workspace status indicator shown as a dot on the switcher tab
    // (e.g. an editor's pending-commit/save state). Not persisted.
    private workspaceStatus: Map<string, string> = new Map();

    // Focus tracking
    private focusedPaneId: string | null = null;
    private focusedSurfaceId: string | null = null;

    constructor(
        containerId: string,
        config: ShellConfig,
        storageKey: string = 'catalog-shell-config'
    ) {
        const containerEl = document.getElementById(containerId);
        if (!containerEl) {
            throw new Error(`Container element '${containerId}' not found`);
        }
        this.container = containerEl;
        this.config = config;
        this.storageKey = storageKey;

        this.typeRegistry = new PaneTypeRegistry();
        this.actionRegistry = new ActionRegistry();
        this.events = new EventEmitter();
        this.breakpoints = config.breakpoints ?? DEFAULT_BREAKPOINTS;

        // Initialize default workspace
        this.activeWorkspaceId = config.defaultWorkspace;

        // Register builtin actions
        createBuiltinActions().forEach(action => this.actionRegistry.register(action));

        // Initialize popout manager (before loadConfiguration so it can receive pending restores)
        this.popoutManager = new PopoutManager(this);

        // Load persisted configuration
        this.loadConfiguration();

        // Set up resize observer
        this.setupResizeObserver();

        // Note: render() is NOT called here - call start() after registering pane types
    }

    /**
     * Start the shell after pane types have been registered.
     * This performs the initial render.
     */
    start(): void {
        this.render();
    }

    // ========================================================================
    // ShellAPI Implementation
    // ========================================================================

    getActiveWorkspace(): string {
        return this.activeWorkspaceId;
    }

    setActiveWorkspace(workspaceId: string): void {
        const workspace = this.allWorkspaceDefs().find(w => w.id === workspaceId);
        if (!workspace) {
            throw new Error(`Unknown workspace: ${workspaceId}`);
        }

        if (this.activeWorkspaceId !== workspaceId) {
            if (this.focusedPaneId) {
                this.workspaceFocusedPane.set(this.activeWorkspaceId, this.focusedPaneId);
            }
            this.activeWorkspaceId = workspaceId;
            this.saveConfiguration();
            this.renderWorkspace(workspace);
            this.restoreWorkspaceFocus(workspaceId);
            // Unconditionally: workspace-scoped actions (ActionDefinition.
            // workspaces) change with the workspace, not with the focus, and
            // a workspace with nothing to focus must still re-render the bar.
            this.renderActionBar();
            this.events.emit('workspace-changed', { workspaceId });
        }
    }

    getWorkspaces(): string[] {
        const screenSize = this.getScreenSize();
        return this.allWorkspaceDefs()
            .filter(w => {
                if (this.hiddenWorkspaces.has(w.id)) return false;
                if (!w.enabledSizes || w.enabledSizes.length === 0) {
                    return false; // On-demand workspaces not shown in switcher
                }
                return w.enabledSizes.includes(screenSize);
            })
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            .map(w => w.id);
    }

    getAllWorkspaces(): string[] {
        return this.allWorkspaceDefs().map(w => w.id);
    }

    /** All workspace definitions: static config + runtime-added (dynamic) ones. */
    private allWorkspaceDefs(): WorkspaceDefinition[] {
        return this.runtimeWorkspaces.length === 0
            ? this.config.workspaces
            : [...this.config.workspaces, ...this.runtimeWorkspaces];
    }

    /** Look up a workspace definition by id (static or runtime). */
    getWorkspaceDef(workspaceId: string): WorkspaceDefinition | null {
        return this.allWorkspaceDefs().find(w => w.id === workspaceId) ?? null;
    }

    /**
     * Add a dynamic workspace at runtime (e.g. an on-demand editor) and, by
     * default, activate it. Persisted across reloads via
     * StoredShellConfig.runtimeWorkspaces.
     */
    addWorkspace(def: WorkspaceDefinition, opts?: { activate?: boolean }): void {
        if (!this.getWorkspaceDef(def.id)) {
            this.runtimeWorkspaces.push(def);
        }
        this.renderWorkspaceSwitcher();
        this.saveConfiguration();
        if (opts?.activate !== false) this.setActiveWorkspace(def.id);
    }

    /**
     * Remove a dynamic workspace and tear down its surfaces/panes. No-op for
     * static (config) workspaces. The root pane's onDestroy cascades to its
     * nested surface + panes.
     */
    removeWorkspace(workspaceId: string): void {
        const idx = this.runtimeWorkspaces.findIndex(w => w.id === workspaceId);
        if (idx < 0) return;
        const wasActive = this.activeWorkspaceId === workspaceId;

        const paneIds = this.workspacePaneInstances.get(workspaceId);
        if (paneIds) for (const pid of [...paneIds]) this.unregisterPaneInstance(pid);
        this.workspacePaneInstances.delete(workspaceId);

        const rootSurface = this.workspaceRootSurfaces.get(workspaceId);
        if (rootSurface) this.unregisterSurface(rootSurface.surfaceId);
        this.workspaceSurfaceInstances.delete(workspaceId);
        this.workspaceRootSurfaces.delete(workspaceId);
        this.workspaceElements.get(workspaceId)?.remove();
        this.workspaceElements.delete(workspaceId);
        delete this.storedWorkspaceConfigs[workspaceId];

        this.runtimeWorkspaces.splice(idx, 1);

        if (wasActive) {
            const visible = this.getWorkspaces();
            const fallback = visible[0] ?? this.config.defaultWorkspace;
            this.activeWorkspaceId = fallback;
            const ws = this.getWorkspaceDef(fallback);
            if (ws) this.renderWorkspace(ws);
            this.events.emit('workspace-changed', { workspaceId: fallback });
        } else {
            this.renderWorkspaceSwitcher();
        }
        this.saveConfiguration();
        // Fire after the workspace is gone (so open/closed checks read false).
        // The precise close origin — shared by the tab × and any programmatic
        // removeWorkspace — distinct from the generic `workspace-changed`.
        this.events.emit('workspace-closed', { workspaceId });
    }

    /** Hide or show a workspace at runtime. If the hidden workspace is active, switches to the default. */
    setWorkspaceHidden(workspaceId: string, hidden: boolean): void {
        if (hidden) {
            this.hiddenWorkspaces.add(workspaceId);
            if (this.activeWorkspaceId === workspaceId) {
                const visible = this.getWorkspaces();
                if (visible.length > 0) this.setActiveWorkspace(visible[0]);
            }
        } else {
            this.hiddenWorkspaces.delete(workspaceId);
        }
        this.renderWorkspaceSwitcher();
    }

    getWorkspaceLabel(workspaceId: string): string {
        const workspace = this.allWorkspaceDefs().find(w => w.id === workspaceId);
        return workspace?.label ?? workspaceId;
    }

    getScreenSize(): ScreenSize {
        if (this.screenSizeOverride) {
            return this.screenSizeOverride;
        }

        // Check fullscreen
        if (document.fullscreenElement) {
            return 'full';
        }

        const width = this.container.clientWidth || window.innerWidth;

        if (width <= this.breakpoints.compact) {
            return 'compact';
        }
        if (width <= this.breakpoints.medium) {
            return 'medium';
        }
        return 'full';
    }

    setScreenSizeOverride(size: ScreenSize | null): void {
        if (this.screenSizeOverride !== size) {
            this.screenSizeOverride = size;
            this.saveConfiguration();
            this.events.emit('screen-size-changed', { screenSize: this.getScreenSize() });
            // Re-render with new size
            const workspace = this.allWorkspaceDefs().find(w => w.id === this.activeWorkspaceId);
            if (workspace) {
                this.renderWorkspace(workspace);
            }
        }
    }

    getPaneType(typeId: string): PaneType | null {
        return this.typeRegistry.get(typeId);
    }

    registerPaneType(type: PaneType): void {
        this.typeRegistry.register(type);
    }

    registerAction(action: ActionDefinition): void {
        this.actionRegistry.register(action);
    }

    executeAction(actionId: string, context?: ActionContext): void {
        const ctx = context ?? this.createActionContext();
        this.actionRegistry.execute(actionId, ctx).then(() => {
            this.events.emit('action-executed', { actionId });
        }).catch(err => {
            console.error(`Error executing action '${actionId}':`, err);
        });
    }

    getAvailableActions(context?: ActionContext): ActionDefinition[] {
        const ctx = context ?? this.createActionContext();
        return this.actionRegistry.getAvailable(ctx);
    }

    getPaneInstance(instanceId: string): PaneInstance | null {
        return this.paneInstances.get(instanceId) ?? null;
    }

    getSurfaceForPane(instanceId: string): SurfaceAPI | null {
        const pane = this.paneInstances.get(instanceId);
        if (!pane?.parentSurfaceId) {
            return null;
        }
        return this.surfaceInstances.get(pane.parentSurfaceId) ?? null;
    }

    on(event: ShellEvent, callback: ShellEventCallback): () => void {
        return this.events.on(event, callback);
    }

    emit(event: ShellEvent, data?: unknown): void {
        this.events.emit(event, data);
    }

    // ========================================================================
    // Persistence
    // ========================================================================

    refreshActionBar(): void {
        this.renderActionBar();
    }

    addActionBarElement(element: HTMLElement): void {
        this.actionBarPrefixElements.push(element);
        this.renderActionBar();
    }

    refreshPaneActions(instanceId: string): void {
        const surface = this.getSurfaceForPane(instanceId);
        if (surface) {
            surface.refreshPaneActions?.(instanceId);
            return;
        }
        // A workspace root belongs to no surface; its actions live in the
        // title strip this shell mounted for it.
        this.refreshWorkspaceRootActions(instanceId);
    }

    saveConfiguration(): void {
        const workspaceConfigs: { [workspaceId: string]: StoredWorkspaceConfig } = {};

        // Save layout config for each cached workspace
        for (const [workspaceId, rootSurface] of this.workspaceRootSurfaces) {
            // A workspace whose root is a module container (not a shell
            // surface) records a null here — it has no layout to save.
            if (!rootSurface) continue;
            const layoutConfig = this.getLayoutConfigFromSurface(rootSurface);
            if (layoutConfig) {
                const screenSize = this.getScreenSize();
                workspaceConfigs[workspaceId] = {
                    layouts: {
                        [screenSize]: layoutConfig,
                    },
                    customized: true,
                };
            }
        }

        const config: StoredShellConfig = {
            version: SHELL_VERSION,
            configVersion: this.config.configVersion,
            activeWorkspace: this.activeWorkspaceId,
            screenSizeOverride: this.screenSizeOverride,
            workspaceConfigs,
            runtimeWorkspaces: this.runtimeWorkspaces.length > 0 ? this.runtimeWorkspaces : undefined,
            popoutWindows: this.popoutManager.getPopoutState(),
        };

        try {
            localStorage.setItem(this.storageKey, JSON.stringify(config));
        } catch (e) {
            console.error('Failed to save shell configuration:', e);
        }
    }

    private getLayoutConfigFromSurface(surface: SurfaceAPI): GridLayoutConfig | FlowLayoutConfig | null {
        // Check if surface has getLayoutConfig method
        if ('getLayoutConfig' in surface && typeof (surface as any).getLayoutConfig === 'function') {
            return (surface as any).getLayoutConfig();
        }
        return null;
    }

    loadConfiguration(): void {
        try {
            const stored = localStorage.getItem(this.storageKey);
            if (!stored) return;

            const config: StoredShellConfig = JSON.parse(stored);

            // Version check
            if (config.version.major !== SHELL_VERSION.major) {
                console.warn('Shell config version mismatch, using defaults');
                return;
            }

            // Config version check — module bumps this when layout configs change
            if (this.config.configVersion !== undefined && config.configVersion !== this.config.configVersion) {
                console.warn(`Config version changed (${config.configVersion} → ${this.config.configVersion}), using defaults`);
                return;
            }

            // Restore dynamic workspaces FIRST so a persisted active editor id
            // (and later lookups) resolve against the merged set.
            this.runtimeWorkspaces = config.runtimeWorkspaces ?? [];

            // Restore state
            if (config.activeWorkspace && this.allWorkspaceDefs().find(w => w.id === config.activeWorkspace)) {
                this.activeWorkspaceId = config.activeWorkspace;
            }
            this.screenSizeOverride = config.screenSizeOverride ?? null;

            // Store workspace configs for later restoration
            this.storedWorkspaceConfigs = config.workspaceConfigs ?? {};

            // Stash popout windows for deferred restoration (after workspace renders)
            if (config.popoutWindows?.length && this.popoutManager) {
                console.log('[Shell] Stashing', config.popoutWindows.length, 'popout(s) for deferred restore');
                this.popoutManager.restorePopouts(config.popoutWindows);
            } else {
                console.log('[Shell] No popouts to restore. popoutWindows:', config.popoutWindows?.length ?? 0, 'popoutManager:', !!this.popoutManager);
            }
        } catch (e) {
            console.error('Failed to load shell configuration:', e);
        }
    }

    /**
     * Get stored layout config for a workspace
     */
    getStoredLayoutConfig(workspaceId: string): GridLayoutConfig | FlowLayoutConfig | null {
        const wsConfig = this.storedWorkspaceConfigs[workspaceId];
        if (!wsConfig?.layouts) return null;

        const screenSize = this.getScreenSize();
        // Try current screen size first, then fall back to any available
        return wsConfig.layouts[screenSize]
            ?? wsConfig.layouts.full
            ?? wsConfig.layouts.medium
            ?? wsConfig.layouts.compact
            ?? null;
    }

    exportConfiguration(): ExportedShellConfig {
        const config: StoredShellConfig = {
            version: SHELL_VERSION,
            activeWorkspace: this.activeWorkspaceId,
            screenSizeOverride: this.screenSizeOverride,
            workspaceConfigs: {},
        };

        const exported: ExportedShellConfig = {
            version: SHELL_VERSION,
            config,
            exportedAt: new Date().toISOString(),
        };

        if (this.config.onBeforeExport) {
            exported.appData = this.config.onBeforeExport();
        }

        return exported;
    }

    importConfiguration(exported: ExportedShellConfig): ImportResult {
        try {
            if (exported.version.major !== SHELL_VERSION.major) {
                return {
                    success: false,
                    error: `Incompatible version: ${exported.version.major}.${exported.version.minor} (expected ${SHELL_VERSION.major}.x)`,
                };
            }

            const config = exported.config;

            // Validate workspace exists
            if (config.activeWorkspace && !this.allWorkspaceDefs().find(w => w.id === config.activeWorkspace)) {
                return {
                    success: false,
                    error: `Unknown workspace: ${config.activeWorkspace}`,
                };
            }

            // Apply configuration
            this.activeWorkspaceId = config.activeWorkspace;
            this.screenSizeOverride = config.screenSizeOverride ?? null;

            if (exported.appData && this.config.onAfterImport) {
                this.config.onAfterImport(exported.appData);
            }

            this.saveConfiguration();
            this.render();

            return { success: true };
        } catch (e) {
            return {
                success: false,
                error: `Import failed: ${e}`,
            };
        }
    }

    // ========================================================================
    // Rendering
    // ========================================================================

    private render(): void {
        clearElement(this.container);

        this.rootElement = createElement('div', {
            className: 'cs-shell',
            id: `shell-${generateId('root')}`,
        });

        // Create header with workspace switcher
        const header = this.createHeader();
        this.rootElement.appendChild(header);

        // Create main content area
        this.rootSurfaceContainer = createElement('div', {
            className: 'cs-shell-content',
        });
        this.rootElement.appendChild(this.rootSurfaceContainer);

        this.container.appendChild(this.rootElement);

        // Initialize modal and toast managers
        this.modalManager = new ModalManager(this.rootElement);
        this.toastManager = new ToastManager(document.body);

        // Render active workspace
        const workspace = this.allWorkspaceDefs().find(w => w.id === this.activeWorkspaceId);
        if (workspace) {
            this.renderWorkspace(workspace);
        }
    }

    private createHeader(): HTMLElement {
        const header = createElement('div', { className: 'cs-shell-header' });

        // Workspace switcher
        this.workspaceSwitcherElement = createElement('div', { className: 'cs-workspace-switcher' });
        this.renderWorkspaceSwitcher();
        header.appendChild(this.workspaceSwitcherElement);

        // Action bar
        this.actionBarElement = createElement('div', { className: 'cs-action-bar' });
        this.renderActionBar();
        header.appendChild(this.actionBarElement);

        return header;
    }

    private renderWorkspaceSwitcher(): void {
        if (!this.workspaceSwitcherElement) return;
        clearElement(this.workspaceSwitcherElement);

        const visibleWorkspaces = this.getWorkspaces();

        for (const workspaceId of visibleWorkspaces) {
            const workspace = this.getWorkspaceDef(workspaceId)!;
            const isActive = workspaceId === this.activeWorkspaceId;

            const btn = createElement('button', {
                className: `cs-workspace-btn ${isActive ? 'cs-active' : ''}`,
                onClick: () => this.setActiveWorkspace(workspaceId),
                onContextMenu: (e) => {
                    const rootSurface = this.workspaceRootSurfaces.get(workspaceId);
                    if (rootSurface && typeof (rootSurface as any).showContextMenuAt === 'function') {
                        e.preventDefault();
                        (rootSurface as any).showContextMenuAt(e);
                    }
                },
            });

            // Status (e.g. an editor's pending-commit/save state) colors the label text.
            const status = this.workspaceStatus.get(workspaceId);
            btn.appendChild(createElement('span', {
                className: `cs-workspace-label${status ? ` cs-status-${status}` : ''}`,
                textContent: workspace.label,
            }));

            // Close icon for dynamic/closable workspaces.
            if (workspace.closable) {
                btn.appendChild(createElement('span', {
                    className: 'cs-workspace-close',
                    attributes: { title: 'Close' },
                    children: [icons.close()],
                    onClick: (e) => {
                        e.stopPropagation();
                        void this.requestCloseWorkspace(workspaceId);
                    },
                }));
            }

            this.workspaceSwitcherElement.appendChild(btn);
        }
    }

    /** Set a status dot ('orange'|'green'|'none'|...) on a workspace's switcher tab. */
    setWorkspaceStatus(workspaceId: string, status: string): void {
        const norm = status && status !== 'none' ? status : '';
        if ((this.workspaceStatus.get(workspaceId) ?? '') === norm) return;
        if (norm) this.workspaceStatus.set(workspaceId, norm);
        else this.workspaceStatus.delete(workspaceId);
        this.renderWorkspaceSwitcher();
    }

    /** Close a dynamic workspace from its switcher ×, honoring the root pane's confirmClose. */
    private async requestCloseWorkspace(workspaceId: string): Promise<void> {
        const paneIds = this.workspacePaneInstances.get(workspaceId);
        if (paneIds) {
            for (const pid of paneIds) {
                const inst = this.paneInstances.get(pid);
                const type = inst ? this.typeRegistry.get(inst.typeId) : null;
                if (type?.confirmClose) {
                    if (!(await type.confirmClose(inst!))) return;
                    break;
                }
            }
        }
        this.removeWorkspace(workspaceId);
    }

    private renderActionBar(): void {
        if (!this.actionBarElement) return;
        clearElement(this.actionBarElement);

        // Custom prefix elements (app-provided)
        for (const el of this.actionBarPrefixElements) {
            this.actionBarElement.appendChild(el);
        }

        const ctx = this.createActionContext();
        const toolbarActions = this.actionRegistry.getToolbarActions(ctx);
        const menuActions = this.actionRegistry.getMenuActions(ctx);

        // Primary toolbar actions. A write in flight (`busy`) renders exactly
        // as a disabled button whose label and tooltip are what the action
        // says it is doing — sugar over disabled + title, not a second
        // mechanism (rule 4). An action `isEnabled` rejects keeps its label
        // and borrows `disabledReason` for the tooltip (rule 3).
        for (const action of toolbarActions) {
            const busy = action.busy?.(ctx);
            // A busy action is disabled whatever `isEnabled` would say, and
            // the predicate is not asked twice over for it.
            const enabled = busy === undefined && this.actionRegistry.isEnabled(action.actionId, ctx);
            const blocked = busy === undefined && !enabled ? action.disabledReason?.(ctx) : undefined;
            const btn = createElement('button', {
                className: 'cs-action-btn',
                textContent: busy ?? action.label,
                attributes: {
                    title: busy ?? blocked
                        ?? (action.shortcut ? `${action.label} (${action.shortcut})` : action.label),
                },
                onClick: () => this.executeAction(action.actionId),
            });
            if (!enabled) {
                btn.disabled = true;
            }
            this.actionBarElement.appendChild(btn);
        }

        // More menu (gear icon dropdown)
        if (menuActions.length > 0) {
            if (!this.moreMenu) {
                this.moreMenu = createDropdownMenu({
                    icon: icons.gear(),
                    title: 'More actions',
                });
            }
            this.moreMenu.setItems(menuActions.map(action => {
                const busy = action.busy?.(ctx);
                const enabled = busy === undefined && this.actionRegistry.isEnabled(action.actionId, ctx);
                const blocked = busy === undefined && !enabled ? action.disabledReason?.(ctx) : undefined;
                return {
                    label: busy ?? action.label,
                    title: busy ?? blocked,
                    disabled: !enabled,
                    onClick: () => this.executeAction(action.actionId),
                };
            }));
            this.actionBarElement.appendChild(this.moreMenu.element);
        }
    }

    private renderWorkspace(workspace: WorkspaceDefinition): void {
        if (!this.rootSurfaceContainer) return;

        // Hide all existing workspace elements
        for (const [wsId, element] of this.workspaceElements) {
            element.style.display = 'none';
        }

        // Check if this workspace is already cached
        const cachedElement = this.workspaceElements.get(workspace.id);
        if (cachedElement) {
            // Restore cached workspace
            cachedElement.style.display = '';
            this.rootSurface = this.workspaceRootSurfaces.get(workspace.id) ?? null;

            // Update workspace switcher and action bar
            this.renderWorkspaceSwitcher();
            this.renderActionBar();
            return;
        }

        // First time rendering this workspace - create it
        // Track instances for this workspace
        this.workspacePaneInstances.set(workspace.id, new Set());
        this.workspaceSurfaceInstances.set(workspace.id, new Set());

        // Clear rootSurface so the new workspace's surface becomes the root
        this.rootSurface = null;

        // Create root pane instance from workspace spec
        const rootInstance = this.typeRegistry.createInstance(workspace.rootPaneSpec);
        this.registerPaneInstance(rootInstance);
        this.workspacePaneInstances.get(workspace.id)!.add(rootInstance.instanceId);

        // Render root pane
        const rootType = this.typeRegistry.get(rootInstance.typeId);
        if (!rootType) {
            console.error(`Unknown root pane type: ${rootInstance.typeId}`);
            return;
        }

        // Create a container for this workspace
        const workspaceContainer = createElement('div', {
            className: 'cs-workspace-content',
            attributes: { 'data-workspace-id': workspace.id },
        });

        const rootElement = rootType.render(this.createRenderContext(rootInstance));

        rootInstance.element = rootElement;
        // A module container used as a workspace root has no title row of its
        // own (a grid cell's tab strip / a flow pane's header is what carries
        // one). Give it a strip above the root so its workspace-wide verbs have
        // a home. Built AFTER render() — unlike the surfaces, which build the
        // title row first — so state the root created while rendering is
        // already there for tabBarActions to read.
        this.renderWorkspaceRootHeader(rootInstance, rootType, workspaceContainer);
        workspaceContainer.appendChild(rootElement);
        this.rootSurfaceContainer.appendChild(workspaceContainer);

        // Cache this workspace
        this.workspaceElements.set(workspace.id, workspaceContainer);
        this.workspaceRootSurfaces.set(workspace.id, this.rootSurface!);

        // Update workspace switcher
        this.renderWorkspaceSwitcher();

        // Restore popout windows (deferred until workspace is rendered)
        this.popoutManager.executePendingRestores();
    }

    /**
     * Mount `.cs-workspace-root-header` (title + `tabBarActions`) above a
     * workspace root that is a module container — `kind: 'container'` and not
     * one of the shell's own surfaces, whose panes carry their own title rows.
     * No-op for everything else.
     */
    private renderWorkspaceRootHeader(
        rootInstance: PaneInstance,
        rootType: PaneType,
        workspaceContainer: HTMLElement,
    ): void {
        if (rootType.kind !== 'container') return;
        if (rootType.typeId === 'grid-surface' || rootType.typeId === 'flow-surface') return;
        if (!rootType.tabBarActions) return;

        const actionsContainer = createElement('div', { className: 'cs-tab-bar-actions' });
        for (const action of rootType.tabBarActions(rootInstance)) {
            actionsContainer.appendChild(action);
        }

        const headerElement = createElement('div', {
            className: 'cs-workspace-root-header',
            children: [
                createElement('span', {
                    className: 'cs-workspace-root-title',
                    textContent: rootInstance.title,
                    // The strip does not wrap either; a truncated title keeps
                    // its tooltip.
                    attributes: { title: rootInstance.title },
                }),
                actionsContainer,
            ],
        });

        // The root usually sizes itself to 100% height; make the container a
        // column so the strip sits above it instead of pushing it out of view.
        workspaceContainer.classList.add('cs-workspace-content--with-header');
        workspaceContainer.appendChild(headerElement);
        this.workspaceRootHeaders.set(rootInstance.instanceId, headerElement);
    }

    /**
     * Re-run a workspace root's `tabBarActions` and swap them into its title
     * strip in place — the root-pane counterpart of a surface's
     * `refreshPaneActions`.
     */
    private refreshWorkspaceRootActions(instanceId: string): void {
        const headerElement = this.workspaceRootHeaders.get(instanceId);
        if (!headerElement) return;
        const instance = this.paneInstances.get(instanceId);
        const type = instance ? this.typeRegistry.get(instance.typeId) : null;
        const container = headerElement.querySelector<HTMLElement>('.cs-tab-bar-actions');
        if (!instance || !type || !container) return;
        clearElement(container);
        for (const action of type.tabBarActions?.(instance) ?? []) {
            container.appendChild(action);
        }
        const titleEl = headerElement.querySelector('.cs-workspace-root-title');
        if (titleEl) {
            titleEl.textContent = instance.title;
            titleEl.setAttribute('title', instance.title);
        }
    }

    // ========================================================================
    // Instance Management
    // ========================================================================

    registerPaneInstance(instance: PaneInstance): void {
        this.paneInstances.set(instance.instanceId, instance);
    }

    createRenderContext(instance: PaneInstance, surface?: SurfaceAPI): PaneTypeRenderContext {
        let disposables = this.paneDisposables.get(instance.instanceId);
        if (!disposables) {
            disposables = new DisposableStore();
            this.paneDisposables.set(instance.instanceId, disposables);
        }
        return { instance, shell: this, surface, disposables };
    }


    unregisterPaneInstance(instanceId: string): void {
        this.workspaceRootHeaders.delete(instanceId);
        const instance = this.paneInstances.get(instanceId);
        if (instance) {
            // Auto-dispose managed resources
            const disposables = this.paneDisposables.get(instanceId);
            disposables?.dispose();
            this.paneDisposables.delete(instanceId);

            // Call type's destroy hook (for backward compat / additional cleanup)
            const type = this.typeRegistry.get(instance.typeId);
            type?.onDestroy?.(instance);

            this.paneInstances.delete(instanceId);
        }
    }

    registerSurface(surface: SurfaceAPI): void {
        this.surfaceInstances.set(surface.surfaceId, surface);
        if (!this.rootSurface) {
            this.rootSurface = surface;
        }
    }

    unregisterSurface(surfaceId: string): void {
        this.surfaceInstances.delete(surfaceId);
    }

    // ========================================================================
    // Focus Management
    // ========================================================================

    setFocusedPane(instanceId: string | null, surfaceId: string | null): void {
        const oldPaneId = this.focusedPaneId;
        this.focusedPaneId = instanceId;
        this.focusedSurfaceId = surfaceId;

        // Notify old pane of deactivation
        if (oldPaneId && oldPaneId !== instanceId) {
            const oldInstance = this.paneInstances.get(oldPaneId);
            if (oldInstance) {
                const type = this.typeRegistry.get(oldInstance.typeId);
                type?.onDeactivate?.(oldInstance);
            }
        }

        // Notify new pane of activation
        if (instanceId) {
            const newInstance = this.paneInstances.get(instanceId);
            if (newInstance) {
                const type = this.typeRegistry.get(newInstance.typeId);
                type?.onActivate?.(newInstance);
                this.events.emit('pane-activated', { instanceId, surfaceId });
            }
        }

        // Update action bar
        this.renderActionBar();
    }

    /**
     * Focus the pane this workspace last had focused, else its root surface's
     * active pane, else nothing — never a pane from the workspace just left.
     */
    private restoreWorkspaceFocus(workspaceId: string): void {
        const surface = this.workspaceRootSurfaces.get(workspaceId) ?? this.rootSurface;
        const remembered = this.workspaceFocusedPane.get(workspaceId);
        if (remembered && this.paneInstances.has(remembered)) {
            const owner = this.getSurfaceForPane(remembered);
            this.setFocusedPane(remembered, owner?.surfaceId ?? surface?.surfaceId ?? null);
            return;
        }
        const active = surface?.getActivePane() ?? null;
        this.setFocusedPane(active?.instanceId ?? null, active ? surface!.surfaceId : null);
    }

    getFocusedPane(): PaneInstance | null {
        return this.focusedPaneId ? this.paneInstances.get(this.focusedPaneId) ?? null : null;
    }

    getFocusedSurface(): SurfaceAPI | null {
        return this.focusedSurfaceId ? this.surfaceInstances.get(this.focusedSurfaceId) ?? null : null;
    }

    // ========================================================================
    // Action Context
    // ========================================================================

    private createActionContext(): ActionContext {
        const focusedPane = this.getFocusedPane() ?? undefined;
        let capabilities: string[] = [];
        if (focusedPane) {
            const paneType = this.typeRegistry.get(focusedPane.typeId);
            if (paneType) capabilities = paneType.capabilities;
        }
        return {
            focusedPane,
            focusedSurface: this.getFocusedSurface() ?? undefined,
            capabilities,
            shell: this,
        };
    }

    // ========================================================================
    // Resize Handling
    // ========================================================================

    private setupResizeObserver(): void {
        let currentScreenSize = this.getScreenSize();

        const handleResize = debounce(() => {
            const newSize = this.getScreenSize();

            // Only process if screen size category actually changed
            if (newSize !== currentScreenSize) {
                const previousSize = currentScreenSize;
                currentScreenSize = newSize;
                this.events.emit('screen-size-changed', { screenSize: newSize });

                // Apply new layout for the current workspace if it has per-breakpoint layouts
                this.applyLayoutForScreenSize(newSize, previousSize);
            }
        }, 100);

        const observer = new ResizeObserver(handleResize);
        observer.observe(this.container);

        // Also handle fullscreen changes
        document.addEventListener('fullscreenchange', () => {
            handleResize();
        });
    }

    /**
     * Apply the appropriate layout for the given screen size.
     * Only applies if the workspace has per-breakpoint layouts defined
     * and the user hasn't customized the layout for this size.
     */
    private applyLayoutForScreenSize(screenSize: ScreenSize, _previousSize: ScreenSize): void {
        const workspace = this.allWorkspaceDefs().find(w => w.id === this.activeWorkspaceId);
        if (!workspace?.layouts) return;

        // Check if there's a stored (user-customized) layout for this screen size
        const storedConfig = this.storedWorkspaceConfigs[this.activeWorkspaceId];
        if (storedConfig?.layouts?.[screenSize]) {
            // User has customized the layout for this size, don't override
            return;
        }

        // Get the layout config for the new screen size
        const layoutForSize = workspace.layouts[screenSize];
        if (!layoutForSize) return;

        // Get the root surface and apply the new layout
        const rootSurface = this.workspaceRootSurfaces.get(this.activeWorkspaceId);
        if (!rootSurface) return;

        // Apply layout based on surface type
        if (layoutForSize.type === 'grid' && 'initializeLayout' in rootSurface) {
            (rootSurface as any).initializeLayout(layoutForSize);
        } else if (layoutForSize.type === 'flow' && 'initializeLayout' in rootSurface) {
            (rootSurface as any).initializeLayout(layoutForSize);
        }
    }

    // ========================================================================
    // Cleanup
    // ========================================================================

    destroy(): void {
        // Close all popout windows
        this.popoutManager.closeAllPopouts();

        // Close all modals and toasts
        this.modalManager?.dispose();
        this.toastManager?.dispose();
        this.modalManager = null;
        this.toastManager = null;

        // Clean up all instances
        for (const [instanceId] of this.paneInstances) {
            this.unregisterPaneInstance(instanceId);
        }

        // Clear DOM
        clearElement(this.container);

        // Clear event listeners
        this.events.removeAllListeners();
    }

    // ========================================================================
    // Modal API
    // ========================================================================

    /** Show a modal with arbitrary content. Returns a handle for programmatic control. */
    showModal(content: HTMLElement, options?: ModalOptions): ModalHandle {
        if (!this.modalManager) throw new Error('Shell not started');
        return this.modalManager.show(content, options);
    }

    /** Show a confirmation dialog. Resolves to true (confirm) or false (cancel/dismiss). */
    confirm(options: ConfirmOptions): Promise<boolean> {
        if (!this.modalManager) throw new Error('Shell not started');
        return this.modalManager.confirm(options);
    }

    /**
     * Show a one-input dialog. Resolves to the entered text, or `null` when the
     * user cancels/dismisses. The sibling of `confirm` for the cases that need
     * a reason or a type-to-confirm (`mustEqual`).
     */
    prompt(options: PromptOptions): Promise<string | null> {
        if (!this.modalManager) throw new Error('Shell not started');
        return this.modalManager.prompt(options);
    }

    /** Close the topmost modal */
    closeModal(): void {
        this.modalManager?.closeTop();
    }

    /** Close all modals */
    closeAllModals(): void {
        this.modalManager?.closeAll();
    }

    // ========================================================================
    // Toast API
    // ========================================================================

    /** Show a toast notification */
    toast(options: ToastOptions): ToastHandle;
    toast(message: string, variant?: 'info' | 'success' | 'warning' | 'error'): ToastHandle;
    toast(optionsOrMessage: ToastOptions | string, variant?: 'info' | 'success' | 'warning' | 'error'): ToastHandle {
        if (!this.toastManager) throw new Error('Shell not started');
        if (typeof optionsOrMessage === 'string') {
            return this.toastManager.show({ message: optionsOrMessage, variant: variant ?? 'info' });
        }
        return this.toastManager.show(optionsOrMessage);
    }

    // ========================================================================
    // Public Accessors for Internal Use
    // ========================================================================

    getPopoutManager(): PopoutManager {
        return this.popoutManager;
    }

    openStandalonePopout(config: StandalonePopoutConfig): void {
        this.popoutManager.openStandalonePopout(config);
    }

    getTypeRegistry(): PaneTypeRegistry {
        return this.typeRegistry;
    }

    getActionRegistry(): ActionRegistry {
        return this.actionRegistry;
    }

    getConfig(): ShellConfig {
        return this.config;
    }

    getDevToolsEnabled(): boolean {
        return this.config.devToolsEnabled ?? false;
    }

    getRootSurface(): SurfaceAPI | null {
        return this.rootSurface;
    }

    getWorkspaceRootSurface(workspaceId: string): SurfaceAPI | null {
        return this.workspaceRootSurfaces.get(workspaceId) ?? null;
    }

    getSurface(surfaceId: string): SurfaceAPI | null {
        return this.surfaceInstances.get(surfaceId) ?? null;
    }
}
